import axios, { AxiosRequestConfig } from 'axios';

import { API_URL, BENEFITS_URL, HOST_API } from 'src/config-global';
import { AxiosHttpClient } from 'src/http';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

export const api = new AxiosHttpClient(API_URL);
export const apiBenefits = new AxiosHttpClient(BENEFITS_URL);

// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: 'auth/cn-admin/get-data',
    login: 'auth/cn-admin/login',
    register: '/api/auth/register',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  banners: {
    list: 'benefits/content-control/home-banners',
    details: (userId: string) => `benefits/content-control/home-banners/details?userId=${userId}`,
    update: 'benefits/content-control/home-banners',
  },
  users: {
    admList: 'auth/cn-admin/all',
    deleteAdm: 'auth/cn-admin/delete-user',
    shopkeepersList: 'auth/shopkeeper/all',
    shopkeeperDetails: (id: string) => `auth/shopkeeper/details?userId=${id}`,
    updateShopkeeper: 'auth/shopkeeper',
    registerShopkeeper: 'auth/shopkeeper',
    deleteAdms: 'auth/cn-admin/delete-many-user',
    createAdm: 'auth/cn-admin/create-user',
    updateAdm: 'auth/cn-admin/update-user',
    admToEdit: 'auth/cn-admin/details',
    changeUserHome: 'auth/cn-admin/change-user-home',
    clientList: 'auth/cn-admin/clients',
    promotersList: 'auth/cn-admin/promoters',
    lpPromoterList: 'auth/cn-admin/all-lp-promoters',
    lpPromoterDetails: 'auth/cn-admin/lp-promoter-details',
    generatePromoterCode: 'auth/cn-admin/generate-promoter-code',
    lpPromoterCreate: 'auth/cn-admin/create-lp-promoter',
    lpPromoterUpdate: 'auth/cn-admin/update-lp-promoter',
    promotersCreate: 'auth/cn-admin/create-promoter',
    promotersUpdate: 'auth/cn-admin/update-promoter',
    promotersImport: 'auth/cn-admin/import-promoters',
    promotersDelete: 'auth/cn-admin/delete-promoter',
    promotersResetPassword: 'auth/cn-admin/reset-promoter-password',
    promoterDetails: 'auth/cn-admin/promoter-details',
    biomeClientsFiltered: 'auth/biome-user/getFiltered',
    registerBiomeUser: 'auth/biome-user/create',
    registerManyBiomeUsers: 'auth/biome-user/bulk-creation',
    editBiomeUser: (id: string) => `auth/biome-user/edit/${id}`,
    vivaClientsFiltered: 'auth/cn-admin/clients-filtered',
    lpParticipants: {
      list: 'auth/cn-admin/all-lp-exhibitor-shopkeepers',
      create: 'auth/cn-admin/create-lp-exhibitor-shopkeeper',
      update: 'auth/cn-admin/update-lp-exhibitor-shopkeeper',
      details: (id: string) => `auth/cn-admin/lp-exhibitor-shopkeeper-details?userId=${id}`,
      byBusinessUnit: 'auth/cn-admin/lp-exhibitor-shopkeeper-by-bu',
      byEvent: 'auth/cn-admin/lp-exhibitor-shopkeeper-by-event',
    },
    userTierChanges: `tiers/user-tier-changes`,
  },
  files: {
    upload: 'auth/files/upload-single',
    uploadLegacy: 'auth/files/upload-single-legacy',
  },
  benefits: {
    promotions: {
      promotionsList: 'benefits/promotion/getAll?textSearch=exp',
      promotionDetails: (id: string) => `benefits/promotion/getById/${id}?textSearch=exp`,
      createPromotion: 'benefits/promotion/create',
      updatePromotion: (id: string) => `benefits/promotion/update/${id}`,
    },
    coupons: {
      couponsList: 'benefits/coupons/coupons-list',
      lpCouponsList: 'benefits/coupons/lp-coupons-list',
      coupomDetails: (id: string) => `benefits/coupons/coupon-details/${id}`,
      createCoupons: 'benefits/coupons/create-coupon',
      updateCoupon: 'benefits/coupons/coupon-update',
      // exportData: 'benefits/coupons/export-adm',
      exportData: 'adm-reports/export-coupons-adm',
      parkingsList: 'benefits/parkings/all-parkings',
    },
    events: {
      eventsList: 'benefits/events/events-list',
      eventDetails: (id: string) => `benefits/events/event-details/${id}`,
      createUpdateEvent: 'benefits/events',
      exportData: 'benefits/events/report',
    },
    reports: {
      reportsList: 'benefits/reports/reports-list',
      unifiedBenefitsList: 'benefits/reports/unified-benefits-list',
    },
    lpEvents: {
      lpEventsList: 'benefits/lp-events/all-active-events',
      lpEventDetails: `benefits/lp-events/lp-event-details`,
      createLpEvent: 'benefits/lp-events/create-lp-event',
      updateLpEvent: 'benefits/lp-events/update-lp-event',
      exportData: 'benefits/lp-events/report',
      businessUnits: 'benefits/lp-events/all-active-business-units',
      allBusinessUnits: 'benefits/lp-events/all-business-units',
      lpEventsByBusinessUnit: `benefits/lp-events/lp-events-by-business-unit`,
      businessUnitById: 'benefits/lp-events/get-bu-by-id',
      lpEventsByBusinessUnits: 'benefits/lp-events/get-events-by-bus',
      banners: {
        bannersByApprovalStatus: 'benefits/lp-events-banners/by-approval-status',
        approveLpBanner: 'benefits/lp-events-banners/approve-draft-banner',
        refuseLpBanner: 'benefits/lp-events-banners/decline-draft-banner',
        benefitsListByType: 'benefits/all-lp-events-benefits',
        bannerDetailsById: 'benefits/lp-events-banners/by-id',
        createBanner: 'benefits/lp-events-banners/create-draft-banner',
        editBanner: 'benefits/lp-events-banners/update-draft-banner',
        changeBannerStatus: 'benefits/lp-events-banners/change-approved-banner-status',
      },
    },
    collectibles: {
      collectionList: 'benefits/collections/collection-list',
      collectionDetails: (id: string) => `benefits/collections/collection-details/${id}`,
      collectionPeriods: 'benefits/collections/get-periods',
      createCollection: 'benefits/collections/create-collection',
      updateCollection: 'benefits/collections/update-collection',
      createCollectible: 'benefits/collectibles/create-collectible',
      collectiblesList: (collectionId: string) =>
        `benefits/collectibles/collectibles-list/${collectionId}`,
      collectibleDetails: (id: string) => `benefits/collectibles/collectible-details/${id}`,
      updateCollectible: 'benefits/collectibles/update-collectible',
      updateCollectibleType: 'benefits/collectibles/update-collectible-type',
      exportData: 'adm-reports/export-collections-adm',
    },
    benefits: {
      benefitsList: 'benefits/admin-filtered',
      adminFilteredRewards: 'benefits/admin-filtered-rewards',
      highlightConfiguration: 'benefits/admin-highlights',
      highlightConfigurationById: (contentSession: string) =>
        `benefits/admin-highlights/${contentSession}`,
      bulkEditSessions: 'benefits/admin-bulk-edit/sessions',
      bulkEditSession: (bulkSessionKey: string) =>
        `benefits/admin-bulk-edit/sessions/${bulkSessionKey}`,
      bulkEditBatch: (bulkSessionKey: string) =>
        `benefits/admin-bulk-edit/sessions/${bulkSessionKey}/batches`,
      bulkEditErrors: (bulkSessionKey: string) =>
        `benefits/admin-bulk-edit/sessions/${bulkSessionKey}/errors`,
      userRedemptions: 'benefits/my-trades',
      exportData: 'adm-reports/export-benefits-adm',
      changeStatus: 'benefits/change-benefit-status',
    },
  },
  invoices: {
    userInvoices: 'invoices/user-invoices',
    invoiceDetails: 'invoices/adm-invoice-detail',
  },
  trails: {
    seasons: {
      list: 'achievements/seasons',
      activeOrUpcomingTrailCombinations:
        'achievements/seasons/active-or-upcoming/trail-combinations',
      invalidPeriods: 'achievements/seasons/invalid-periods',
      details: (id: string) => `achievements/seasons/${id}`,
      create: 'achievements/seasons',
      update: (id: string) => `achievements/seasons/${id}`,
    },
    trails: {
      list: 'achievements/trails',
      activeSearch: 'achievements/trails/active-search',
      details: (id: string) => `achievements/trails/${id}`,
      create: 'achievements/trails',
      update: (id: string) => `achievements/trails/${id}`,
      updateStatus: (id: string) => `achievements/trails/${id}/status`,
    },
  },
  personalization: {
    appTexts: {
      list: 'benefits/app-text/all',
      one: `benefits/app-text/details`,
      edit: `benefits/app-text/update`,
    },
    terms: {
      one: 'auth/terms/lat',
      new: 'auth/terms/create',
    },
    promoterTerms: {
      one: 'auth/promoter-terms/lat',
      new: 'auth/promoter-terms/create',
    },
    regulation: {
      promotional: {
        list: 'auth/promotional/all',
        new: 'auth/promotional/create',
        details: 'auth/promotional/details',
        edit: 'auth/promotional/update',
      },
    },
    faq: {
      list: 'auth/faq/all',
      new: 'auth/faq/create',
      details: 'auth/faq/details',
      edit: 'auth/faq/update',
    },
    onboarding: {
      details: 'benefits/content-control/onboarding-details',
      update: 'benefits/content-control/onboarding-update',
    },
    homeBands: {
      list: 'auth/home-band/all',
      one: `auth/home-band/details`,
      edit: `auth/home-band/`,
      create: `auth/home-band/`,
    },
    homeBanners: {
      list: 'auth/home-banner-page/all',
      one: `auth/home-banner-page/details`,
      edit: `auth/home-banner-page/`,
      create: `auth/home-banner-page/`,
      changeStatus: (id: string) => `auth/home-banner-page/${id}/status`,
      deleteBanners: 'auth/home-banner-page',
    },
  },
  missions: {
    list: 'missions/missions/admin/all',
    details: (id: string) => `missions/missions/admin/details?missionId=${id}`,
    create: 'missions/missions/admin',
    update: 'missions/missions/admin',
  },
};
